import { useCallback, useEffect, useRef } from 'react';
import type { ChangeEvent, MutableRefObject, Ref } from 'react';

type UseTextAreaParams = {
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  ref?: Ref<HTMLTextAreaElement>;
  referer?: Ref<HTMLTextAreaElement>;
  value?: string;
};

// Auto-grow the textarea to fit its content: reset the height so it can shrink,
// then size it to the scroll height.
function adjustHeight(element: HTMLTextAreaElement | null): void {
  if (!element) {
    return;
  }

  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
}

// Forward a node to an external ref, supporting both callback and object refs.
function assignExternalRef(target: Ref<HTMLTextAreaElement> | undefined, node: HTMLTextAreaElement | null): void {
  if (!target) {
    return;
  }

  if (typeof target === 'function') {
    target(node);
  } else {
    (target as MutableRefObject<HTMLTextAreaElement | null>).current = node;
  }
}

function useTextArea({ onChange, ref, referer, value }: UseTextAreaParams) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  // Single callback ref: keep an internal handle (needed to measure/resize) and
  // forward the node to the external ref (`referer` from react-hook-form, or the
  // forwarded `ref`). `referer` wins to mirror `useInputText`.
  const refTextArea = useCallback(
    (node: HTMLTextAreaElement | null) => {
      internalRef.current = node;
      assignExternalRef(referer ?? ref, node);
    },
    [ref, referer]
  );

  // Keep the height in sync with the controlled `value` (and on mount).
  useEffect(() => {
    const element = internalRef.current;
    if (!element) {
      return;
    }

    if (value) {
      adjustHeight(element);
    } else {
      element.style.height = 'auto';
    }
  }, [value]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      adjustHeight(e.currentTarget);
      onChange?.(e);
    },
    [onChange]
  );

  return {
    handleChange,
    refTextArea,
  };
}

export default useTextArea;
